import React, { InputHTMLAttributes } from "react";
import classNames from "classnames";

import styles from "./Input.module.css";

interface Props
  extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  type?:
    | "text"
    | "tel"
    | "textarea"
    | "search"
    | "number"
    | "password"
    | "email"
    | "date"
    | undefined;
  name: string;
  label?: string;
  placeholder?: string;
  helpText?: string;
  error?: string;
  success?: string;
  full?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  multiline?: boolean;
  active?: boolean;
}

const Input: React.FC<Props> = ({
  type = "text",
  value = "",
  name,
  label,
  placeholder,
  helpText,
  error,
  success,
  disabled,
  multiline,
  active,
  onChange,
  autocomplete,
  ...props
}) => {
  const showHelpText = helpText || error || success;
  const Node = multiline ? "textarea" : "input";

  const autoExpand = (target: HTMLElement | null) => {
    if (target === null) {
      return;
    }
    target.style.height = "inherit";
    target.style.height = `${target.scrollHeight}px`;
  };

  const onChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (multiline) {
      autoExpand(e.target);
    }
    if (onChange) {
      onChange(e);
    }
  };

  const inputClass = classNames({
    [styles.input]: true,
    [styles["-disabled"]]: disabled,
    [styles["-error"]]: error,
    [styles["-success"]]: success && !error,
    [styles["-touched"]]: value !== "",
    [styles["-active"]]: active,
    [styles["-show-message"]]: showHelpText,
    [styles["-multiline"]]: multiline,
  });

  return (
    <div className={inputClass}>
      <label htmlFor={name} className={styles.label}>
        {label}
      </label>
      <Node
        className={styles.field}
        type={type}
        name={name}
        id={name}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={onChangeHandler}
        autoComplete={autocomplete}
        aria-invalid={!!error}
        aria-describedby={`input-${name}-feedback`}
        {...props}
      />

      <div className={styles.shadow} />
      <div id={`input-${name}-feedback`}>
        {showHelpText && (
          <div className={styles.message} aria-live="polite">
            <p className="sr">
              {(error || success || helpText) && `${error && "Feil: "}${label}`}
            </p>
            {error || success || helpText}
          </div>
        )}
      </div>
    </div>
  );
};

export default Input;
